import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { FiltersBar, Spark, Loading, ExpBtn, CARD, LBL, SEL, fmtAht, fmtNum, fmtDelta, fq, downloadPost } from "./axiom";

const VIEWS = [["lob", "By LOB"], ["category", "By Category"], ["sub_category", "By Sub Category"], ["language", "By Language"], ["location", "By Location"], ["matrix", "Lang Matrix"], ["weekly", "Weekly Trend"]];

const cellStyle = (val, benchmark) => {
  if (val == null || benchmark == null) return {};
  if (val <= benchmark) return { background: "rgb(var(--th-mint) / 0.15)", color: "rgb(var(--th-mint))", fontWeight: 700 };
  return { background: "rgb(var(--th-coral) / 0.15)", color: "rgb(var(--th-coral))", fontWeight: 700 };
};

export const BenchmarkTab = ({ summary, filters, setFilters }) => {
  const [view, setView] = useState("lob");
  const [pastWeeks, setPastWeeks] = useState(8);
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [pendingMonth, setPendingMonth] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/aht/settings").then((r) => { setSettings(r.data); setPendingMonth(r.data.benchmark_month); }); }, []);
  const load = useCallback(() => {
    setData(null);
    api.get(`/aht/benchmark?view=${view}&past_weeks=${pastWeeks}&${fq(filters)}`).then((r) => setData(r.data));
  }, [view, filters, pastWeeks]);
  useEffect(() => { if (settings) load(); }, [load, settings]);

  const apply = async () => {
    try {
      const { data: s } = await api.put("/aht/settings", { ...settings, benchmark_month: pendingMonth });
      setSettings(s);
      toast.success(pendingMonth ? `Benchmarks applied — locked to ${pendingMonth}` : "Benchmark month cleared");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const resetAll = async () => {
    const { data: s } = await api.put("/aht/settings", { ...settings, benchmark_month: "", tolerance: 0 });
    setSettings(s); setPendingMonth("");
    toast.success("Benchmark configuration reset");
    load();
  };

  const exportExcel = async () => {
    if (!data?.rows) return;
    setBusy("excel");
    const cols = data.weeks ? ["Segment", "Calls", "Benchmark (s)", ...data.weeks, "Δ Latest (s)"] : ["Language", ...(data.cols || [])];
    const rows = data.weeks
      ? data.rows.map((r) => [r.name, r.calls, r.benchmark != null ? Math.round(r.benchmark) : "", ...data.weeks.map((w) => (r.cells[w] != null ? Math.round(r.cells[w]) : "")), r.delta != null ? Math.round(r.delta) : ""])
      : data.rows.map((r) => [r.name, ...(data.cols || []).map((c) => (r.cells[c] != null ? Math.round(r.cells[c]) : ""))]);
    await downloadPost("/aht/export/excel", { filename: "AHT_Benchmark.xlsx", sheets: [{ name: `Benchmark ${view}`, columns: cols, rows }] }, "AHT_Benchmark.xlsx");
    setBusy(null);
  };
  const exportPpt = async () => {
    setBusy("ppt");
    await downloadPost(`/aht/export/benchmark-ppt?${fq(filters)}`, {}, "AHT_Benchmarking.pptx");
    setBusy(null);
  };

  if (!settings) return <Loading />;
  const wkLabel = (w) => `WK ${w.split("-W")[1]}`;

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-bold text-thstrong">AHT Benchmark Configuration</h2>
        <div className="flex gap-2">
          <ExpBtn testId="aht-benchmark-export-excel" onClick={exportExcel} busy={busy === "excel"} color="purple">Export to Excel</ExpBtn>
          <button data-testid="aht-benchmark-reset" onClick={resetAll} className="rounded-lg border border-thborder/12 bg-thcard px-4 py-2 text-xs font-semibold text-thbody hover:text-red-500">↺ Reset All</button>
          <button data-testid="aht-benchmark-apply" onClick={apply} className="rounded-lg bg-thcyan px-4 py-2 text-xs font-semibold text-white hover:bg-thcyan">✓ Apply Benchmarks</button>
        </div>
      </div>

      <div className={`${CARD} mb-6 p-5`}>
        <div className="flex flex-wrap items-center gap-6">
          <div>
            <label className={`${LBL} mb-1.5 block`}>Benchmark Basis</label>
            <select data-testid="aht-benchmark-month" value={pendingMonth ?? ""} onChange={(e) => setPendingMonth(e.target.value)} className={`${SEL} min-w-[220px]`}>
              <option value="">Not set</option>
              {(summary.months || []).map((m) => <option key={m} value={m}>{m} — lock as benchmark</option>)}
            </select>
          </div>
          <div>
            <label className={`${LBL} mb-1.5 block`}>Tolerance (+s)</label>
            <input data-testid="aht-benchmark-tolerance" type="number" min="0" value={settings.tolerance}
              onChange={(e) => setSettings({ ...settings, tolerance: Number(e.target.value) || 0 })} className={`${SEL} w-20`} />
          </div>
          <p className="max-w-2xl text-[11px] leading-relaxed text-thmute">
            {settings.benchmark_month ? <>Benchmark is locked to <b className="text-amber-600">{settings.benchmark_month}</b> — each row's benchmark = its AHT that month, <b>+{settings.tolerance}s tolerance</b> for rows whose latest-week AHT already exceeds it. Applies to all views, the Dashboard & exports, and persists across data uploads.</> : "No benchmark month locked. Select a month and click Apply Benchmarks."}
          </p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-1 border-b border-thborder/12">
        {VIEWS.map(([k, label]) => (
          <button key={k} data-testid={`aht-benchmark-view-${k}`} onClick={() => { setData(null); setView(k); }}
            className={`px-4 py-2.5 text-xs font-semibold transition-colors ${view === k ? "border-b-2 border-amber-500 text-amber-600" : "text-thmute hover:text-thstrong"}`}>
            {k === "weekly" ? "" : ""}{label}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <FiltersBar summary={summary} filters={filters} setFilters={setFilters} showMonths={false} />
        <div className="mb-6 flex items-end gap-3">
          <div>
            <label className={`${LBL} mb-1.5 block`}>Past Weeks</label>
            <select data-testid="aht-benchmark-weeks" value={pastWeeks} onChange={(e) => setPastWeeks(Number(e.target.value))} className={SEL}>
              {[4, 8, 12, 16].map((w) => <option key={w} value={w}>{w} weeks</option>)}
            </select>
          </div>
          <ExpBtn testId="aht-export-benchmark-ppt" onClick={exportPpt} busy={busy === "ppt"} color="indigo">Export Benchmark PPT</ExpBtn>
          <p className="pb-2 text-[11px] text-thdim">
            Benchmark = <span className="font-semibold text-amber-600">{settings.benchmark_month || "not set"}</span> · cells below / above · seconds
          </p>
        </div>
      </div>

      {!data ? <Loading /> : view === "matrix" ? (
        !data.cols ? <Loading /> : (
          <div className={`${CARD} overflow-x-auto`}>
            <table className="w-full text-left" data-testid="aht-table-benchmark-matrix">
              <thead><tr className="border-b border-thborder/8">
                <th className={`${LBL} px-5 py-3`}>Language</th>
                {data.cols.map((c) => <th key={c} className={`${LBL} px-4 py-3 text-center`}>{c}</th>)}
              </tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.name} className="border-b border-thborder/6">
                    <td className="px-5 py-2.5 text-xs font-bold text-thbody">{r.name}</td>
                    {data.cols.map((c) => <td key={c} className="font-mono-hud px-4 py-2.5 text-center text-xs font-semibold text-thbody">{r.cells[c] != null ? Math.round(r.cells[c]) : "—"}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : !data.weeks ? <Loading /> : (
        <>
          <div className={`${CARD} overflow-x-auto`}>
            <table className="w-full text-left" data-testid={`aht-table-benchmark-${view}`}>
              <thead><tr className="border-b border-thborder/8 bg-thhover/5/60">
                <th className={`${LBL} px-5 py-3`}>{view === "weekly" ? "LOB" : "Segment"}</th>
                <th className={`${LBL} px-3 py-3 text-right`}>Calls</th>
                <th className="font-mono-hud px-3 py-3 text-[10px] uppercase tracking-[0.2em] text-amber-500">Benchmark</th>
                {data.weeks.map((w) => <th key={w} className={`${LBL} whitespace-nowrap px-3 py-3 text-center`}>{wkLabel(w)}</th>)}
                <th className={`${LBL} px-3 py-3 text-right`}>Δ Latest</th>
                <th className={`${LBL} px-3 py-3 text-center`}>Trend</th>
              </tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.name} className="border-b border-thborder/6">
                    <td className="max-w-[220px] truncate whitespace-nowrap px-5 py-2.5 text-xs font-bold text-thbody">{r.name}</td>
                    <td className="font-mono-hud px-3 py-2.5 text-right text-xs text-thmute">{fmtNum(r.calls)}</td>
                    <td className="font-mono-hud px-3 py-2.5 text-center text-xs font-bold text-amber-500">{r.benchmark != null ? Math.round(r.benchmark) : "—"}</td>
                    {data.weeks.map((w) => (
                      <td key={w} className="px-1 py-1 text-center">
                        <span className="font-mono-hud block rounded px-2 py-1.5 text-xs" style={cellStyle(r.cells[w], r.benchmark)}>
                          {r.cells[w] != null ? Math.round(r.cells[w]) : "—"}
                        </span>
                      </td>
                    ))}
                    <td className={`font-mono-hud px-3 py-2.5 text-right text-xs font-bold ${r.status === "above" ? "text-red-500" : r.status === "tolerance" ? "text-amber-500" : r.delta != null ? "text-thmint" : "text-thfaint"}`}>
                      {r.delta != null ? `${r.delta > 0 ? "▲ +" : "▼ -"}${Math.abs(Math.round(r.delta))}s` : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-center"><Spark series={data.weeks.map((w) => r.cells[w])} color="#F59E0B" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="font-mono-hud mt-3 text-[10px] uppercase tracking-[0.15em] text-thdim">
            Last {data.weeks.length} week(s) · {data.rows.length} row(s) · Δ latest = most recent week vs benchmark{settings.tolerance ? ` (+${settings.tolerance}s tolerance)` : ""}.
          </p>
        </>
      )}
    </div>
  );
};
